import { Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getCompaniesWithCampaignInfo } from 'API/company';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import { Roles, ZoomLevels } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import * as XLSX from 'xlsx';

const CollapsibleText: React.FC<{ text: string; maxLength: number }> = ({ text, maxLength = 100 }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setIsExpanded(!isExpanded);
  };

  const displayedText = isExpanded ? text : text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');

  return (
    <div>
      {displayedText}
      {text.length > maxLength && (
        <PaiLink
          text={isExpanded ? t('less') : t('more')}
          inline={!isExpanded}
          ml={!isExpanded ? '4px' : undefined}
          size={'md'}
          variant={'primary'}
          underline={'hover'}
          action={toggleExpand}
        />
      )}
    </div>
  );
};

interface Tenant {
  id: string;
  companyName: string;
  createdAt: string;
  users: string[];
  totalCampaigns: number;
  runningCampaigns: number;
}

const AdminUserOverview: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { role } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  const abortController = useRef<AbortController | null>(null);

  const fetchTenants = async () => {
    setTenantsLoading(true);
    abortController.current = new AbortController();
    const res = await getCompaniesWithCampaignInfo(abortController.current.signal);
    if (!res.error) {
      setTenants(res || []);
    } else {
      handleOpenErrorDialog(res.error, '/tenants/current/campaign-info', 'get');
    }
    setTenantsLoading(false);
  };

  const handleDownload = () => {
    if (_.isEmpty(tenants)) return;

    // Build a sorted copy to mirror the table order
    const exportTenants: Tenant[] = [...tenants];

    const header = [t('company'), t('createdAt'), t('users'), t('campaigns'), t('runningCampaigns')];
    const rows = exportTenants.map((tenant) => {
      const emails = (tenant.users || []).filter((u) => typeof u === 'string' && u.includes('@')).join(', ');
      const created = tenant.createdAt ? new Date(tenant.createdAt) : '';
      return [
        tenant.companyName || '-',
        created,
        emails || '-',
        typeof tenant.totalCampaigns === 'number' ? tenant.totalCampaigns : 0,
        typeof tenant.runningCampaigns === 'number' ? tenant.runningCampaigns : 0,
      ];
    });

    const worksheetData = [header, ...rows];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tenants');

    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const fileName = `tenants_overview_${yyyy}-${mm}-${dd}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'companyName', headerName: t('name'), flex: 1, valueFormatter: (value: string) => value || '-' },
      {
        field: 'createdAt',
        headerName: t('createdAt'),
        flex: 1,
        type: 'dateTime',
        valueGetter: (value: string) => (value ? new Date(value) : null),
        valueFormatter: (value: Date | null) => (value ? formatDateTime(value.toISOString(), 'Pp') : '-'),
      },
      {
        field: 'users',
        headerName: t('users'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CollapsibleText text={(params.value || []).join(', ')} maxLength={50} />
        ),
      },
      { field: 'totalCampaigns', headerName: t('campaigns'), flex: 1, type: 'number', valueFormatter: (value: number) => value || '-' },
      { field: 'runningCampaigns', headerName: t('runningCampaigns'), flex: 1, type: 'number', valueFormatter: (value: number) => value || '-' },
    ],
    [t],
  );

  useEffect(() => {
    fetchTenants();
    return () => {
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
    };
  }, []);

  if (role !== Roles.OWNER) {
    return <Typography p={4}>{t('noPermission', 'You do not have permission to access this page.')}</Typography>;
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.tabContainer}>
          <Stack spacing={2}>
            <AdminSubpageHeader title={t('adminNavUserOverview')} />
            <PaiButton text={t('download')} size={'small'} border={'rounded'} action={handleDownload} disabled={tenantsLoading || _.isEmpty(tenants)} />
          </Stack>
        </div>
        <Stack className={classes.content} gap={2}>
          <DataGrid
            rows={tenants}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            loading={tenantsLoading}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'companyName', sort: 'asc' }] },
            }}
            autoHeight
            localeText={{ noRowsLabel: t('noTenantsFound') }}
          />
        </Stack>
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  tabContainer: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  content: {
    height: '100%',
    padding: '16px 32px',
    overflow: 'auto',
    display: 'flex',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'nowrap',
  },
}));

export default AdminUserOverview;
